import React, { ChangeEventHandler, FunctionComponent, ReactNode } from 'react';

import {
  ASCENDING_ORDER_DIRECTION_LABEL,
  ColumnDefinition,
  DESCENDING_ORDER_DIRECTION_LABEL,
  NO_ORDER_DIRECTION_LABEL,
  TableHeaderColumnTypes,
  TableHeaderSortingDirection,
} from '../Table.constants';
import Checkbox from '../../Checkbox';
import { ASCENDING_ORDER_DIRECTION, DESCENDING_ORDER_DIRECTION, OrderDirection } from '../../../shared/constants';

import styles from './TableHeader.module.scss';

export type TableHeaderProps = {
  columns: ColumnDefinition[];
  onColumnSort?: (columnId: string) => void;
  onRowsSelect?: ChangeEventHandler;
  orderBy?: string;
  orderDirection?: OrderDirection;
  numberOfRows?: number;
  numberOfSelectedRows?: number;
  tableId: string;
};

const TableHeader: FunctionComponent<TableHeaderProps> = ({
  columns,
  onColumnSort,
  onRowsSelect,
  orderBy,
  orderDirection,
  numberOfRows = 0,
  numberOfSelectedRows = 0,
  tableId,
}) => {
  const renderHeader = (): ReactNode => {
    return columns
      .filter((columnDef: ColumnDefinition) => !columnDef.hidden)
      .map((columnDef: ColumnDefinition) => {
        let content: ReactNode = columnDef.title;
        let sortOrder: TableHeaderSortingDirection = NO_ORDER_DIRECTION_LABEL;
        let sortLabel: TableHeaderSortingDirection = DESCENDING_ORDER_DIRECTION_LABEL;

        if (onColumnSort && columnDef.sorting) {
          const sortColumn = (event: React.MouseEvent<HTMLButtonElement>): void => {
            let id = columnDef.id;
            if (tableId === 'leads-list') {
              if (id === 'countryId') {
                id = `user.${id}`;
              }
              if (id === 'name') {
                id = `user.firstName`;
              }
            }
            onColumnSort(id);
          };

          if (columnDef.id === orderBy) {
            if (orderDirection === ASCENDING_ORDER_DIRECTION) {
              sortOrder = DESCENDING_ORDER_DIRECTION_LABEL;
              sortLabel = DESCENDING_ORDER_DIRECTION_LABEL;
            } else if (orderDirection === DESCENDING_ORDER_DIRECTION) {
              sortOrder = ASCENDING_ORDER_DIRECTION_LABEL;
              sortLabel = ASCENDING_ORDER_DIRECTION_LABEL;
            }
          }

          content = (
            <button
              className={['o-table__sort', styles.sortingButton].join(' ')}
              title={`Sort table by "${content}" ${sortLabel}`}
              onClick={sortColumn}
            >
              {content}
            </button>
          );
        }

        const tableHeaderClasses = [];
        let headerDataType = null;

        switch (columnDef.type) {
          case TableHeaderColumnTypes.currency:
          case TableHeaderColumnTypes.numeric: {
            headerDataType = TableHeaderColumnTypes.numeric;
            tableHeaderClasses.push('o-table__cell--numeric');
            break;
          }
          case TableHeaderColumnTypes.status: {
            tableHeaderClasses.push(styles.status);
            break;
          }
          case TableHeaderColumnTypes.selection: {
            tableHeaderClasses.push(styles.selection);
            content = (
              <Checkbox
                name="table-header"
                id="select-all"
                onChange={onRowsSelect}
                value={numberOfSelectedRows === numberOfRows}
              />
            );

            break;
          }
        }

        return (
          <th
            data-o-table-data-type={headerDataType}
            className={tableHeaderClasses.join(' ')}
            key={columnDef.id}
            scope="col"
            role="columnheader"
            aria-sort={sortOrder}
            style={{ width: columnDef.width }}
            title={columnDef.tooltip}
          >
            {content}
          </th>
        );
      });
  };

  return (
    <thead>
      <tr>{renderHeader()}</tr>
    </thead>
  );
};

export default TableHeader;
